import { useCallback, useEffect, useRef, useState } from "react";
import { globalRemoveMaskReal2 } from "../../../../../shared/utils";

// --------------------------
export function detectCoverageType(predial: string, content: string): string {
    const p = Number(predial.replace(",", "."));
    const c = Number(content.replace(",", "."));

    if (p === 90 && c === 10) return "Predio_e_Conteudo_90_10";
    if (p === 80 && c === 20) return "Predio_e_Conteudo_80_20";
    if (p === 70 && c === 30) return "Predio_e_Conteudo_70_30";

    return "Predio_e_Conteudo_Outras";
}

interface Params {
    mode: "create" | "edit";
    formRef: React.RefObject<any>;

    predialPercent: string;
    contentPercent: string;

    setPredialPercent: (v: string) => void;
    setContentPercent: (v: string) => void;

    isLoaderReady: boolean;
}

export function useCoverageTypeFields({
    mode,
    formRef,
    predialPercent,
    contentPercent,
    setPredialPercent,
    setContentPercent,
    isLoaderReady,
}: Params) {

    const editInitRef = useRef(false);

    const [coverageType, setCoverageType] = useState(() =>
        detectCoverageType(predialPercent, contentPercent)
    );

    const [showOutras, setShowOutras] = useState(() =>
        detectCoverageType(predialPercent, contentPercent) === "Predio_e_Conteudo_Outras"
    );

    // -------------------------------------------------------------
    // 1) EDIT MODE — Ação única, só quando loader finaliza
    // -------------------------------------------------------------
    useEffect(() => {
        if (mode !== "edit") return;
        if (!isLoaderReady) return;
        if (editInitRef.current) return;

        console.log("EDIT → Inicializando campos Predial/Conteúdo a partir do loader");

        editInitRef.current = true;

        const detected = detectCoverageType(predialPercent, contentPercent);

        setCoverageType(detected);
        setShowOutras(detected === "Predio_e_Conteudo_Outras");

        formRef.current?.setFieldValue("coverageType", detected);

        // Garantir preenchimento dos campos
        formRef.current?.setFieldValue("PredialPercent", predialPercent);
        formRef.current?.setFieldValue("ContentPercent", contentPercent);

    }, [mode, isLoaderReady, formRef]); // 🔥 NÃO depende dos percentuais aqui!


    // -------------------------------------------------------------
    // 2) CREATE — sempre reprocessar mudanças
    // -------------------------------------------------------------
    useEffect(() => {
        if (mode !== "create") return;

        const detected = detectCoverageType(predialPercent, contentPercent);

        setCoverageType(detected);
        setShowOutras(detected === "Predio_e_Conteudo_Outras");

        formRef.current?.setFieldValue("coverageType", detected);

    }, [mode, predialPercent, contentPercent, formRef]);


    // -------------------------------------------------------------
    // 3) Alterou o SELECT manualmente
    // -------------------------------------------------------------
    const handleTypeChange = useCallback((type: string) => {
        setCoverageType(type);
        const isOutras = type === "Predio_e_Conteudo_Outras";
        setShowOutras(isOutras);

        formRef.current?.setFieldValue("coverageType", type);

        if (isOutras) return;

        let pred = "80";
        let cont = "20";

        if (type === "Predio_e_Conteudo_90_10") { pred = "90"; cont = "10"; }
        if (type === "Predio_e_Conteudo_70_30") { pred = "70"; cont = "30"; }

        const p = pred.replace(".", ",");
        const c = cont.replace(".", ",");

        formRef.current?.setFieldValue("PredialPercent", p);
        formRef.current?.setFieldValue("ContentPercent", c);

        setPredialPercent(p);
        setContentPercent(c);

    }, [formRef, setPredialPercent, setContentPercent]);


    // -------------------------------------------------------------
    // 4) Alterou o campo Prédio manualmente (OUTRAS)
    // -------------------------------------------------------------
    const handlePredialBlur = useCallback((e: any) => {
        if (coverageType !== "Predio_e_Conteudo_Outras") return;

        const value = Number(globalRemoveMaskReal2(e.target.value));
        const predio = Math.min(Math.max(value, 0), 100);
        const conteudo = 100 - predio;

        const p = predio.toFixed(2).replace(".", ",");
        const c = conteudo.toFixed(2).replace(".", ",");

        // 1️⃣ Atualiza estados controlados primeiro
        setPredialPercent(p);
        setContentPercent(c);

        // 2️⃣ Só depois atualiza Unform
        formRef.current?.setFieldValue("PredialPercent", p);
        formRef.current?.setFieldValue("ContentPercent", c);

    }, [coverageType, formRef, setPredialPercent, setContentPercent]);

    // ---------------------------------------------
    // 5) Quando showOutras aparecer na tela (EDIT)
    // ---------------------------------------------
    useEffect(() => {
        if (mode !== "edit") return;
        if (!isLoaderReady) return;
        if (!showOutras) return;

        formRef.current?.setFieldValue("PredialPercent", predialPercent);
        formRef.current?.setFieldValue("ContentPercent", contentPercent);

    }, [showOutras, isLoaderReady, mode, predialPercent, contentPercent, formRef]);


    return {
        coverageType,
        showOutras,
        handleTypeChange,
        handlePredialBlur,
    };
}
